import { formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { MoneyFormatters } from '../../_money/useMoneyFormatters.js';
import type { InvoiceListItemDto } from '../contractsMirror.js';
import { InvoiceStatusBadge } from './InvoiceStatusBadge.js';

/**
 * Invoices list table (legacy `InvoicesPage` table). A real semantic `<table>`
 * — Invoice (number + client sub) · Job · Issued · Due · **Total** (right, mono)
 * · Status (badge) · Attachment · Actions — with a `<caption>`, `<th scope="col">`
 * headers, numeric Total right-aligned, and a readable colspan empty row
 * (A11Y-01). Numerals in IBM Plex Mono. Every color is a locked token.
 *
 * The invoice-number cell links to the detail route. The Attachment column shows
 * a "View PDF" link to the api-host PDF when one is attached. Archive/Restore
 * row actions are deferred to the detail view (the api-host has no archive
 * endpoint yet — see the feature SUMMARY).
 */

const TEXT_TH =
  'px-3 py-2 text-left font-semibold text-muted-foreground text-xs uppercase tracking-wide';
const NUMERIC_TH =
  'px-3 py-2 text-right font-semibold text-muted-foreground text-xs uppercase tracking-wide';

function Dash() {
  return <span className="text-muted-foreground">—</span>;
}

export function InvoicesTable({
  invoices,
  fmt,
}: {
  invoices: InvoiceListItemDto[];
  fmt: MoneyFormatters;
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';

  return (
    <div className="overflow-x-auto rounded-md border border-border bg-card">
      <table className="w-full border-collapse">
        <caption className="sr-only">{t('invoices:table.caption')}</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className={TEXT_TH}>
              {t('invoices:table.invoice')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('invoices:table.job')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('invoices:table.issued')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('invoices:table.due')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('invoices:table.total')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('invoices:table.status')}
            </th>
            <th scope="col" className={TEXT_TH}>
              {t('invoices:table.attachment')}
            </th>
            <th scope="col" className={NUMERIC_TH}>
              {t('invoices:table.actions')}
            </th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((inv) => (
            <tr key={inv.id} className="border-border/60 border-b last:border-b-0">
              <td className="px-3 py-2 text-sm">
                <Link
                  to="/invoices/$id"
                  params={{ id: String(inv.id) }}
                  className="font-semibold font-mono text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {inv.invoiceNumber || t('invoices:table.unnamedInvoice', { id: inv.id })}
                </Link>
                <div className="mt-0.5 text-muted-foreground text-xs">
                  {inv.clientName || t('invoices:table.noClient')}
                </div>
              </td>
              <td className="px-3 py-2 text-foreground text-sm">{inv.jobName || <Dash />}</td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-foreground text-sm">
                {inv.dateIssued ? (
                  formatUiDate(inv.dateIssued, locale, { dateStyle: 'medium' })
                ) : (
                  <Dash />
                )}
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-foreground text-sm">
                {inv.dueDate ? (
                  formatUiDate(inv.dueDate, locale, { dateStyle: 'medium' })
                ) : (
                  <Dash />
                )}
              </td>
              <td className="px-3 py-2 text-right font-mono font-medium text-foreground text-sm">
                {fmt.formatCurrency(inv.amount || 0)}
              </td>
              <td className="px-3 py-2 text-sm">
                <InvoiceStatusBadge
                  status={inv.status}
                  overdue={inv.overdue}
                  archived={Boolean(inv.archivedAt)}
                />
              </td>
              <td className="px-3 py-2 text-sm">
                {inv.attachmentFilename ? (
                  <span className="text-foreground text-xs">{inv.attachmentFilename}</span>
                ) : (
                  <span className="text-muted-foreground text-xs">
                    {t('invoices:table.noAttachment')}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                <Link
                  to="/invoices/$id"
                  params={{ id: String(inv.id) }}
                  className="inline-flex h-11 items-center rounded-md px-3 font-medium text-foreground text-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {t('invoices:actions.view')}
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
